import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  Heart, LogOut, Plus, Calendar, MapPin, Clock, Users, 
  MoreVertical, Pencil, Trash2, Eye, Download, ExternalLink, Repeat 
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { admin, token, logout } = useAuth();
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [form, setForm] = useState({
    title: '',
    description: '',
    date: '',
    time: '',
    location: '',
    student_slots: 0,
    parent_slots: 0,
    signup_genius_url: '',
    recurrence_type: '',
    recurrence_count: 2
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchOpportunities();
  }, []);

  const fetchOpportunities = async () => {
    try {
      const response = await axios.get(`${API}/opportunities`);
      setOpportunities(response.data);
    } catch (error) {
      toast.error('Failed to load opportunities');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form };
      // Only include recurrence if set and count >= 2
      if (!payload.recurrence_type || payload.recurrence_type === 'none') {
        delete payload.recurrence_type;
        delete payload.recurrence_count;
      } else {
        // Ensure count is at least 2 for recurring events
        if (payload.recurrence_count < 2) {
          payload.recurrence_count = 2;
        }
      }
      const response = await axios.post(`${API}/opportunities`, payload, { headers });
      const count = response.data.length;
      toast.success(`${count} opportunity${count > 1 ? 's' : ''} created!`);
      setShowCreateModal(false);
      resetForm();
      fetchOpportunities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create opportunity');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.put(`${API}/opportunities/${selectedOpp.id}`, form, { headers });
      toast.success('Opportunity updated!');
      setShowEditModal(false);
      resetForm();
      fetchOpportunities();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update opportunity');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/opportunities/${selectedOpp.id}`, { headers });
      toast.success('Opportunity deleted');
      setShowDeleteDialog(false);
      setSelectedOpp(null);
      fetchOpportunities();
    } catch (error) {
      toast.error('Failed to delete opportunity');
    }
  };

  const handleExport = async (opp) => {
    try {
      const response = await axios.get(`${API}/signups/${opp.id}/export`, {
        headers,
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${opp.title.replace(/\s+/g, '_')}_signups.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export downloaded!');
    } catch (error) {
      toast.error('Failed to export');
    }
  };

  const resetForm = () => {
    setForm({
      title: '',
      description: '',
      date: '',
      time: '',
      location: '',
      student_slots: 0,
      parent_slots: 0,
      signup_genius_url: '',
      recurrence_type: '',
      recurrence_count: 1
    });
    setSelectedOpp(null);
  };

  const openEditModal = (opp) => {
    setSelectedOpp(opp);
    setForm({
      title: opp.title,
      description: opp.description,
      date: opp.date,
      time: opp.time,
      location: opp.location,
      student_slots: opp.student_slots,
      parent_slots: opp.parent_slots,
      signup_genius_url: opp.signup_genius_url || '',
      recurrence_type: '',
      recurrence_count: 1
    });
    setShowEditModal(true);
  };

  const openDeleteDialog = (opp) => {
    setSelectedOpp(opp);
    setShowDeleteDialog(true);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out');
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const totalVolunteers = opportunities.reduce(
    (sum, opp) => sum + opp.student_signups + opp.parent_signups, 0
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Decorative blobs */}
      <div className="bg-blob w-96 h-96 bg-primary -top-48 -left-48 fixed" />
      <div className="bg-blob w-80 h-80 bg-secondary top-1/2 -right-40 fixed" />

      {/* Header */}
      <header className="relative z-10 border-b border-border/50 bg-background/80 backdrop-blur-sm sticky top-0">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-2xl bg-primary flex items-center justify-center">
                <Heart className="w-5 h-5 text-primary-foreground" />
              </div>
              <h1 className="text-2xl font-extrabold text-foreground font-[Nunito]">GYVC | VolunteerHub</h1>
            </Link>
            <Badge variant="secondary" className="ml-4">Admin</Badge>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground hidden sm:block">
              Welcome, {admin?.name}
            </span>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleLogout}
              className="gap-2"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
          <Card className="rounded-2xl border-border/50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Events</p>
                  <p className="text-3xl font-bold font-[Nunito]" data-testid="total-events">
                    {opportunities.length}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-border/50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Volunteers</p>
                  <p className="text-3xl font-bold font-[Nunito]" data-testid="total-volunteers">
                    {totalVolunteers}
                  </p>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-secondary/30 flex items-center justify-center">
                  <Users className="w-6 h-6 text-secondary-foreground" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="rounded-2xl border-border/50">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Fill Rate</p>
                  <p className="text-3xl font-bold font-[Nunito]" data-testid="fill-rate">
                    {opportunities.length > 0 
                      ? Math.round((totalVolunteers / opportunities.reduce(
                          (sum, o) => sum + o.student_slots + o.parent_slots, 0
                        )) * 100) || 0
                      : 0}%
                  </p>
                </div>
                <div className="w-12 h-12 rounded-2xl bg-accent flex items-center justify-center">
                  <Heart className="w-6 h-6 text-accent-foreground" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Actions Bar */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold font-[Nunito]">Opportunities</h2>
          <Button 
            onClick={() => { resetForm(); setShowCreateModal(true); }}
            className="rounded-full font-bold gap-2"
            data-testid="create-opportunity-btn"
          >
            <Plus className="w-4 h-4" />
            New Opportunity
          </Button>
        </div>

        {/* Opportunities List */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="rounded-3xl animate-pulse">
                <CardHeader><div className="h-6 bg-muted rounded w-3/4"></div></CardHeader>
                <CardContent><div className="h-20 bg-muted rounded"></div></CardContent>
              </Card>
            ))}
          </div>
        ) : opportunities.length === 0 ? (
          <Card className="rounded-3xl border-border/50 py-16 text-center">
            <CardContent>
              <Calendar className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-xl font-bold font-[Nunito] mb-2">No opportunities yet</h3>
              <p className="text-muted-foreground mb-6">Create your first volunteer opportunity to get started.</p>
              <Button 
                onClick={() => { resetForm(); setShowCreateModal(true); }}
                className="rounded-full font-bold gap-2"
              >
                <Plus className="w-4 h-4" />
                Create Opportunity
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {opportunities.map((opp) => (
              <Card 
                key={opp.id} 
                className="opportunity-card rounded-3xl border-border/50"
                data-testid={`admin-opp-card-${opp.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg font-bold font-[Nunito] pr-8">
                      {opp.title}
                    </CardTitle>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`opp-menu-${opp.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/admin/signups/${opp.id}`)} data-testid={`view-signups-${opp.id}`}>
                          <Eye className="w-4 h-4 mr-2" />
                          View Sign-ups
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openEditModal(opp)} data-testid={`edit-opp-${opp.id}`}>
                          <Pencil className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleExport(opp)} data-testid={`export-opp-${opp.id}`}>
                          <Download className="w-4 h-4 mr-2" />
                          Export CSV
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => openDeleteDialog(opp)}
                          className="text-destructive"
                          data-testid={`delete-opp-${opp.id}`}
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <p className="text-sm text-muted-foreground line-clamp-2">{opp.description}</p>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-foreground">
                      <Calendar className="w-4 h-4 text-primary" />
                      <span>{formatDate(opp.date)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-foreground">
                      <Clock className="w-4 h-4 text-primary" />
                      <span>{opp.time}</span>
                    </div>
                    <div className="flex items-center gap-2 text-foreground">
                      <MapPin className="w-4 h-4 text-primary" />
                      <span className="truncate">{opp.location}</span>
                    </div>
                  </div>

                  <div className="flex gap-2 pt-2">
                    <Badge variant="secondary" className="text-xs">
                      Students: {opp.student_signups}/{opp.student_slots}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      Parents: {opp.parent_signups}/{opp.parent_slots}
                    </Badge>
                  </div>
                  
                  {opp.signup_genius_url && (
                    <a 
                      href={opp.signup_genius_url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-xs text-primary hover:underline pt-1"
                      data-testid={`signup-genius-link-${opp.id}`}
                    >
                      <ExternalLink className="w-3 h-3" />
                      SignUpGenius Link
                    </a>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Create/Edit Modal */}
      <Dialog open={showCreateModal || showEditModal} onOpenChange={(open) => {
        if (!open) { setShowCreateModal(false); setShowEditModal(false); resetForm(); }
      }}>
        <DialogContent className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto" data-testid="opportunity-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold font-[Nunito]">
              {showEditModal ? 'Edit Opportunity' : 'Create Opportunity'}
            </DialogTitle>
            <DialogDescription>
              {showEditModal ? 'Update the details below' : 'Fill in the details to create a new volunteer opportunity'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={showEditModal ? handleEdit : handleCreate} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={form.title}
                onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                required
                className="rounded-xl h-11"
                placeholder="e.g., Community Garden Clean-up"
                data-testid="opp-title-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={form.description}
                onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                required
                className="rounded-xl min-h-[80px]"
                placeholder="Describe the volunteer opportunity..."
                data-testid="opp-description-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="date">Date</Label>
                <Input
                  id="date"
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm(f => ({ ...f, date: e.target.value }))}
                  required
                  className="rounded-xl h-11"
                  data-testid="opp-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="time">Time</Label>
                <Input
                  id="time"
                  value={form.time}
                  onChange={(e) => setForm(f => ({ ...f, time: e.target.value }))}
                  required
                  className="rounded-xl h-11"
                  placeholder="e.g., 9:00 AM - 12:00 PM"
                  data-testid="opp-time-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="location">Location</Label>
              <Input
                id="location"
                value={form.location}
                onChange={(e) => setForm(f => ({ ...f, location: e.target.value }))}
                required
                className="rounded-xl h-11"
                placeholder="e.g., Main Street Park"
                data-testid="opp-location-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="student_slots">Student Slots</Label>
                <Input
                  id="student_slots"
                  type="number"
                  min="0"
                  value={form.student_slots}
                  onChange={(e) => setForm(f => ({ ...f, student_slots: parseInt(e.target.value) || 0 }))}
                  required
                  className="rounded-xl h-11"
                  data-testid="opp-student-slots-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="parent_slots">Parent Slots</Label>
                <Input
                  id="parent_slots"
                  type="number"
                  min="0"
                  value={form.parent_slots}
                  onChange={(e) => setForm(f => ({ ...f, parent_slots: parseInt(e.target.value) || 0 }))}
                  required
                  className="rounded-xl h-11"
                  data-testid="opp-parent-slots-input"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="signup_genius_url">SignUpGenius Link (Optional)</Label>
              <Input
                id="signup_genius_url"
                type="url"
                value={form.signup_genius_url}
                onChange={(e) => setForm(f => ({ ...f, signup_genius_url: e.target.value }))}
                className="rounded-xl h-11"
                placeholder="https://www.signupgenius.com/go/..."
                data-testid="opp-signup-genius-url-input"
              />
              <p className="text-xs text-muted-foreground">
                Add a link to the SignUpGenius page for this opportunity
              </p>
            </div>

            {/* Recurrence Options - Only show when creating */}
            {!showEditModal && (
              <div className="space-y-4 p-4 bg-accent/30 rounded-xl">
                <div className="flex items-center gap-2">
                  <Repeat className="w-4 h-4 text-primary" />
                  <Label className="font-semibold">Recurring Event (Optional)</Label>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="recurrence_type">Repeat</Label>
                    <Select
                      value={form.recurrence_type}
                      onValueChange={(value) => setForm(f => ({ ...f, recurrence_type: value }))}
                    >
                      <SelectTrigger className="rounded-xl h-11" data-testid="recurrence-type-select">
                        <SelectValue placeholder="No repeat" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No repeat</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {form.recurrence_type && form.recurrence_type !== 'none' && (
                    <div className="space-y-2">
                      <Label htmlFor="recurrence_count">Number of Events</Label>
                      <Input
                        id="recurrence_count"
                        type="number"
                        min="2"
                        max="52"
                        value={form.recurrence_count}
                        onChange={(e) => setForm(f => ({ ...f, recurrence_count: parseInt(e.target.value) || 2 }))}
                        className="rounded-xl h-11"
                        data-testid="recurrence-count-input"
                      />
                    </div>
                  )}
                </div>
                {form.recurrence_type && form.recurrence_type !== 'none' && form.recurrence_count > 1 && (
                  <p className="text-xs text-muted-foreground">
                    This will create {form.recurrence_count} events, each {form.recurrence_type === 'weekly' ? 'week' : 'month'} apart starting from the selected date.
                  </p>
                )}
              </div>
            )}
            
            <div className="flex gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => { setShowCreateModal(false); setShowEditModal(false); resetForm(); }}
                className="flex-1 rounded-full"
                data-testid="opp-cancel-btn"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={submitting}
                className="flex-1 rounded-full font-bold"
                data-testid="opp-submit-btn"
              >
                {submitting ? 'Saving...' : (showEditModal ? 'Update' : 'Create')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="rounded-3xl" data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-[Nunito]">Delete Opportunity?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{selectedOpp?.title}" and all associated volunteer sign-ups. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full" data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="delete-confirm-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;
